/**
 * VisionProInput — Apple Vision Pro transient-pointer input handler.
 *
 * On Apple Vision Pro, the user's gaze is private by design. There is no
 * continuous eye-tracking API. Instead, when the user performs a pinch gesture
 * the browser adds a new XRInputSource with `targetRayMode: 'transient-pointer'`.
 * The `targetRaySpace` pose captures a gaze ray snapshot (origin + direction)
 * at the exact moment of pinch, and `gripSpace` provides the finger position.
 * When the pinch is released the input source is removed.
 *
 * Usage:
 *   const vpInput = new VisionProInput();
 *   // In XR frame loop:
 *   vpInput.update(session, frame, refSpace, reader);
 *   if (vpInput.isTransientPointerActive()) {
 *     const ray = vpInput.getGazeRay();   // gaze snapshot at pinch moment
 *     const grip = vpInput.getGripPosition(0); // left hand finger position
 *   }
 */

import type { InputReader } from './InputReader.js';

// ─── GazeRay ─────────────────────────────────────────────────────────────────

/**
 * Gaze ray captured from the targetRaySpace of a transient-pointer source.
 * Both origin and direction are in the reference space coordinate system.
 * The direction vector points away from the user toward the gaze target.
 */
export interface GazeRay {
  /** World-space origin of the ray (between the user's eyes). */
  origin: { x: number; y: number; z: number };
  /** Unit direction vector pointing toward the gaze target (forward = -Z convention). */
  direction: { x: number; y: number; z: number };
}

// ─── VisionProInput ──────────────────────────────────────────────────────────

export class VisionProInput {
  /** True if any transient-pointer source was present this frame. */
  private _active = false;

  /** True if a transient-pointer source was present last frame. */
  private _wasActive = false;

  /**
   * Gaze ray state. `_gazeRay` returns null when no ray was captured this
   * frame; otherwise it points at `_gazeRayScratch`. Using a fixed scratch
   * object (instead of `{ origin: {...}, direction: {...} }` per frame)
   * saves 3 allocations per XR frame (90 Hz on Vision Pro = 270/sec).
   *
   * The scratch is returned by reference from `getGazeRay()`; callers MUST
   * NOT retain it across frames — its fields are mutated in place.
   */
  private _gazeRay: GazeRay | null = null;
  private _gazeRayScratch: GazeRay = {
    origin: { x: 0, y: 0, z: 0 },
    direction: { x: 0, y: 0, z: -1 },
  };

  /** Grip positions reused across frames — set null when no pose this frame. */
  private _gripPositionLeft: { x: number; y: number; z: number } | null = null;
  private _gripPositionRight: { x: number; y: number; z: number } | null = null;
  private _gripLeftScratch = { x: 0, y: 0, z: 0 };
  private _gripRightScratch = { x: 0, y: 0, z: 0 };

  // ─── Public API ────────────────────────────────────────────────────

  /**
   * Process all XR input sources for the current frame.
   *
   * Call this once per XR frame inside the rAF callback, before reading
   * `isTransientPointerActive()`, `getGazeRay()`, or `getGripPosition()`.
   *
   * Writes `VP_Select` press / release actions to the InputReader on the
   * frame the transient-pointer source appears / disappears.
   *
   * @param session  - The active XRSession (may be null/undefined outside XR).
   * @param frame    - The current XRFrame.
   * @param refSpace - The reference space used for pose calculations.
   * @param reader   - InputReader that receives the `VP_Select` action signals.
   */
  update(
    session: any,   // XRSession — typed as any; no XR type bundle required
    frame: any,     // XRFrame
    refSpace: any,  // XRReferenceSpace
    reader: InputReader,
  ): void {
    // Carry over active state for edge-detection, then reset this frame's data
    this._wasActive = this._active;
    this._active = false;
    this._gazeRay = null;
    this._gripPositionLeft = null;
    this._gripPositionRight = null;

    if (!session || !frame || !refSpace) return;

    const inputSources = session.inputSources;
    if (!inputSources) return;

    for (let i = 0; i < inputSources.length; i++) {
      const source = inputSources[i];

      // Only process transient-pointer sources (Vision Pro pinch gesture)
      if (source.targetRayMode !== 'transient-pointer') continue;

      this._active = true;

      // ── Gaze ray from targetRaySpace ────────────────────────────────
      if (source.targetRaySpace) {
        const pose = frame.getPose(source.targetRaySpace, refSpace);
        if (pose) {
          _extractGazeRayInto(pose, this._gazeRayScratch);
          this._gazeRay = this._gazeRayScratch;
        }
      }

      // ── Grip (finger) position from gripSpace ───────────────────────
      if (source.gripSpace) {
        const gripPose = frame.getPose(source.gripSpace, refSpace);
        if (gripPose) {
          const gp = gripPose.transform.position;
          // Write into the per-hand scratch and point the nullable slot at
          // it, rather than allocating a fresh `{x,y,z}` every frame.
          if (source.handedness === 'left') {
            this._gripLeftScratch.x = gp.x;
            this._gripLeftScratch.y = gp.y;
            this._gripLeftScratch.z = gp.z;
            this._gripPositionLeft = this._gripLeftScratch;
          } else {
            this._gripRightScratch.x = gp.x;
            this._gripRightScratch.y = gp.y;
            this._gripRightScratch.z = gp.z;
            this._gripPositionRight = this._gripRightScratch;
          }
        }
      }
    }

    // ── Edge detection → VP_Select action ──────────────────────────────
    if (this._active && !this._wasActive) {
      reader._setPressed('VP_Select');
    } else if (!this._active && this._wasActive) {
      reader._setReleased('VP_Select');
    }
  }

  /**
   * Returns true if any transient-pointer source is present this frame.
   * Equivalent to "the user is currently pinching".
   */
  isTransientPointerActive(): boolean {
    return this._active;
  }

  /**
   * Returns the gaze ray captured at the moment of pinch, or null when no
   * transient-pointer source is active.
   *
   * The ray is expressed in the reference space passed to `update()`.
   * The direction is normalised and points away from the user (forward = -Z).
   */
  getGazeRay(): GazeRay | null {
    return this._gazeRay;
  }

  /**
   * Returns the grip (pinching-finger) position for the specified hand,
   * or null if that hand has no active transient-pointer source this frame.
   *
   * @param hand - 0 for left hand, 1 for right hand.
   */
  getGripPosition(hand: 0 | 1): { x: number; y: number; z: number } | null {
    return hand === 0 ? this._gripPositionLeft : this._gripPositionRight;
  }

  /**
   * Clear all cached state and release references.
   * Safe to call multiple times.
   */
  dispose(): void {
    this._active = false;
    this._wasActive = false;
    this._gazeRay = null;
    this._gripPositionLeft = null;
    this._gripPositionRight = null;
  }
}

// ─── Private Helpers ──────────────────────────────────────────────────────────

/**
 * Derive a GazeRay from an XRPose.
 *
 * The XRPose orientation quaternion encodes the full rotation of the
 * targetRaySpace. The canonical forward direction for an XR ray is -Z in
 * local space. We rotate (0, 0, -1) by the quaternion to obtain the world-
 * space direction:
 *
 *   v' = q * v * q^-1
 *
 * For a pure quaternion q = (qx, qy, qz, qw) and unit vector v = (0, 0, -1):
 *
 *   vx = -2*(qx*qz + qw*qy)
 *   vy = -2*(qy*qz - qw*qx)  which simplifies to 2*(qw*qx - qy*qz)
 *   vz = -(1 - 2*(qx*qx + qy*qy))
 *
 * The negation at the end collapses into the formula below.
 */
function _extractGazeRayInto(pose: any, out: GazeRay): void {
  const p = pose.transform.position;
  const o = pose.transform.orientation;

  const qx = o.x, qy = o.y, qz = o.z, qw = o.w;

  // Rotate (0, 0, -1) by quaternion: direction = q * (0,0,-1) * q^-1
  const dx = -2 * (qx * qz + qw * qy);
  const dy = -2 * (qy * qz - qw * qx);
  const dz = -(1 - 2 * (qx * qx + qy * qy));

  // Re-normalise to guard against floating-point drift in XR quaternions
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;

  out.origin.x = p.x; out.origin.y = p.y; out.origin.z = p.z;
  out.direction.x = dx / len;
  out.direction.y = dy / len;
  out.direction.z = dz / len;
}
